/*
 * A message that has been written but not acknowledged yet (spec 08, A-08 §3.1).
 *
 * It is drawn as the row it is about to become — the same author, the same time, the same body
 * through `MessageRow`'s `pending` variant — because a placeholder in a different shape makes the
 * timeline jump when the real row replaces it, and for the whole of an undo window it used to be
 * raw markdown in body colour with nobody's name on it. The server has not answered, so there is no
 * render tree yet and `MessageBody` shows the source as plain text; the staged files are not drawn
 * either, since what the tray holds is not yet what a message carries.
 *
 * Under the body is the state line, and the state is the whole story of the send:
 *
 *   held     the undo window is on — a countdown and a real *Undo* button, which is the only way
 *            back that leaves no tombstone. Off unless the account asked for it (A-08 §5).
 *   queued   nothing can leave the device: the socket is down, and "Sending …" would be a claim
 *            the shell's own banner contradicts.
 *   sending  on its way, or waiting its turn behind an older message of this conversation — said
 *            by the tab that dispatches. One tab of a session does (core/drafts/leader.ts), and in
 *            every other the sentence names that: the message is another tab's to send, which is
 *            true, and which tells a person whose message stays where it is where to look.
 *   failed   the server refused it or the retries ran out: *Try again* and *Back to the draft* —
 *            the two things the outbox can do about it, and neither had a control until now.
 *
 * Three of the four say so at once; `sending` waits half a second first. On a healthy
 * socket a send is acknowledged in 20–60 ms, and a row that announced "Sending …" for one frame of
 * every message anybody ever wrote was reporting the network's existence rather than a state worth
 * knowing. The row is not dimmed either, and never was anything but: a message of one's own is a
 * message, and greying it out while the server thinks about it made the timeline blink on every send.
 *
 * A delete on its way out draws nothing: what it will leave behind is the tombstone, and inventing
 * a sentence for the half-second in between would be a second story about the same message.
 */

import type { ReactNode } from "react"
import { currentOutbox, useOutboxLeader } from "../../../core/drafts/outbox.ts"
import type { OutboxItem } from "../../../core/drafts/types.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { Icon } from "../../../core/shell/index.ts"
import { outboxSends } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { useDelayedFlag } from "../../../lib/motion.ts"
import { Button } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import type { MessageView } from "../wire/views.ts"
import { MessageRow, type MessageRowActions, type PendingInfo } from "./MessageRow.tsx"

/** How often the held countdown is redrawn; the number it shows changes once a second. */
const COUNTDOWN_TICK_MS = 250

/**
 * How long a send is given before the row says it is on its way. Longer than `--delay-reveal`, which
 * is for a screen that is being built: half a second is the point at which a person who pressed
 * Enter starts wondering whether anything happened.
 */
export const SENDING_DELAY_MS = 500

/** A pending row has no controls of its own, and `MessageRow` offers none while `pending` is set. */
const NO_ACTIONS: MessageRowActions = {
	copyLink: () => undefined,
	copyMarkdown: () => undefined,
	edit: () => undefined,
	remove: () => undefined,
	markUnreadFrom: () => undefined,
	toggleSelect: () => undefined,
}

export interface PendingRowProps {
	entry: OutboxItem | undefined
	/** The signed-in account: a pending row is always my own message. */
	authorId: number
	authorName: string
}

/** Which of the four the item is in: its own state, plus whether anything can leave at all. */
function stateOf(item: OutboxItem, connected: boolean): PendingInfo["state"] {
	if (item.state === "failed") return "failed"
	if (item.state === "held") return "held"
	return connected ? "sending" : "queued"
}

/**
 * What the state line says, and what it offers to do about it. `sending` is the one state that can
 * answer with nothing at all: until the half second is up there is nothing worth reporting, and the
 * row is simply the message.
 */
function statusLine(
	state: PendingInfo["state"],
	entry: OutboxItem,
	now: number,
	sendingShown: boolean,
	elsewhereShown: boolean,
): ReactNode {
	const outbox = currentOutbox()
	switch (state) {
		case "queued":
			return t("chat.message.queued")
		case "sending":
			if (!sendingShown) return null
			return (
				<>
					<Icon name="clock" size={14} />
					{t(elsewhereShown ? "chat.message.queuedElsewhere" : "chat.message.pending")}
				</>
			)
		case "held":
			return (
				<>
					{t("chat.message.held", {
						seconds: Math.max(0, Math.ceil((entry.sendAfter - now) / 1000)),
					})}
					<Button
						variant="neutral"
						size="sm"
						label={t("chat.message.undo")}
						onClick={() => void outbox?.undo(entry.clientMsgId)}
					/>
				</>
			)
		case "failed":
			return (
				<>
					{t("chat.message.failed")}
					<Button
						variant="neutral"
						size="sm"
						label={t("chat.message.retry")}
						onClick={() => void outbox?.retry(entry.clientMsgId)}
					/>
					<Button
						variant="neutral"
						size="sm"
						label={t("chat.message.backToDraft")}
						onClick={() => void outbox?.discardToDraft(entry.clientMsgId)}
					/>
				</>
			)
	}
}

export function PendingRow({ entry, authorId, authorName }: PendingRowProps) {
	const connection = useModel((model) => model.connection)
	// The one clock of the interface (core/i18n/useNow.ts): an interval that is not a positive
	// number means "read it once", which is every state but the countdown.
	const now = useNow(entry?.state === "held" ? COUNTDOWN_TICK_MS : 0)
	const connected = outboxSends(connection)
	const leads = useOutboxLeader()
	// Asked before the early return, because a hook is: what it reports is whether a send has been
	// in flight long enough to be worth a sentence.
	const sending =
		entry !== undefined &&
		entry.payload.kind !== "delete" &&
		stateOf(entry, connected) === "sending"
	const sendingShown = useDelayedFlag(sending, SENDING_DELAY_MS)
	// Whose send it is, under the same patience: a tab asks for the lead the moment its socket opens
	// and has it a few milliseconds later, and a sentence must not change twice in that time. Only
	// an open connection counts — during the catch-up no tab competes, this one included.
	const elsewhereShown = useDelayedFlag(
		sending && connection === "open" && !leads,
		SENDING_DELAY_MS,
	)

	if (entry === undefined || entry.payload.kind === "delete") return null

	const state = stateOf(entry, connected)
	// The message as it will arrive, minus everything only the server can answer for: no seq, no
	// id, no render tree, no attachment rows.
	const msg: MessageView = {
		id: 0,
		conv: 0,
		seq: 0,
		authorId,
		authorKind: "user",
		kind: "text",
		markdown: entry.payload.markdown,
		clientMsgId: entry.clientMsgId,
		createdAt: entry.createdAt,
		version: 1,
	}

	return (
		<MessageRow
			msg={msg}
			continues={false}
			authorName={authorName}
			mine
			selected={false}
			actions={NO_ACTIONS}
			pending={{ state, statusLine: statusLine(state, entry, now, sendingShown, elsewhereShown) }}
		/>
	)
}
